import React, {ReactElement, useState} from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  FormControl,
  FormHelperText, Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import {Button, Card} from "react-bootstrap";
import {CollaboratorListing} from "../model/CollaboratorListing";
import apiClient from "../../framework/web/ApiClient";
import {CollaboratorType, CollaboratorTypes} from "../model/CollaboratorType";
import Form from "react-bootstrap/esm/Form";

interface PromoteCollabDialogProps {
  onClose: (error: string) => void,
  onCollabPromoted: () => void,
  collabTypes: Array<typeof CollaboratorType[keyof typeof CollaboratorType]>,
  collaborators?: readonly CollaboratorListing[],
  institutes?: readonly string[]
}

export default function PromoteCollabDialog({ onClose,
                                              onCollabPromoted,
                                              collabTypes,
                                              collaborators,
                                              institutes
                                            }: PromoteCollabDialogProps): ReactElement {
  const [error, setError] = useState('');
  const [selectedCollabType, setSelectedCollabType] = useState(collabTypes[0].value)
  const [selectedInstitute, setSelectedInstitute] = useState('');
  const [selectedCollab, setSelectedCollab] = useState(0);
  const [instituteCollabs, setInstituteCollabs] = useState<CollaboratorListing[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);


  const handleSelectInstitute = (e: SelectChangeEvent) => {
    setSelectedInstitute(e.target.value);
    setInstituteCollabs(collaborators?.filter(c => c.instituteName === e.target.value) ?? []);
  }

  const handleSelectCollab = (e: SelectChangeEvent<number>) => {
    setSelectedCollab(e.target.value);
  }

  const handlePromoteCollab = () => {
    if(isProcessing) return;
    setIsProcessing(true);

    if (selectedCollab === 0) {
      setError('Please select a collaborator.');
      setIsProcessing(false);
      return;
    }
    apiClient.post(`/ams/api/v1/collaborators/${selectedCollab}/promote`, {collabType: selectedCollabType})
    .then(onCollabPromoted)
    .catch(e => {
      if (e.status >= 400) {
        console.log("Failed to promote collaborator", e);
        onClose(e.response.data.errors[0].message ?? "Failed to promote collaborator");
      }
    })
    .finally(() => {
      setIsProcessing(false)
    });
  }

  return (
      <Dialog slotProps={{paper: {className: "collab-dialog"}}} open={true}>
        <DialogTitle className={"dialog-header"}>Promote a collaborator to PI/LC</DialogTitle>
        <Box sx={{padding: 3}}>
          <Card className="medium-font" style={{border: 'none'}}>
            <Card.Text>
              <p>Select a collaborator to promote:</p>
            </Card.Text>
          </Card>
          <Grid className={"mt-3"} container columnSpacing={4}>
            {
              CollaboratorTypes.map(ct => (
                  <Grid columns={3}>
                    <Form.Check type={"radio"} name="collabType"
                                checked={selectedCollabType === ct.value}
                                value={ct.value} label={ct.label}
                                onChange={(e) => setSelectedCollabType(e.target.value)}
                                data-testid={`collaboratorType-${ct.value}`}
                                disabled={!collabTypes.includes(ct)}/>
                  </Grid>
              ))
            }
          </Grid>
          <br/>
          <FormControl className="promote-collab-form-control">
            <InputLabel id={"lb-select-institute"}>Institute</InputLabel>
            <Select value={selectedInstitute} label="Institute" labelId="lb-select-institute"
                    onChange={handleSelectInstitute} id="select-institute">
              {institutes?.map(i =>
                  <MenuItem value={i}>{i}</MenuItem>
              )}
            </Select>
          </FormControl>
          <br/>
          <FormControl className="promote-collab-form-control">
            <InputLabel id={"lb-select-collab"}>Collaborator</InputLabel>
            <Select value={selectedCollab} label={"Collaborator"} labelId={"lb-select-collab"}
                    onChange={handleSelectCollab} id="select-collab" error={error.length > 0}
                    disabled={selectedInstitute === ''}>
              {instituteCollabs.map(c =>
                  <MenuItem value={c.collaboratorId}>{c.name}</MenuItem>
              )}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
            {(!error && selectedInstitute.length == 0) &&
                <FormHelperText>Please select an institute.</FormHelperText>}
          </FormControl>
          <div className="d-flex gap-3 center mt-lg-5">
            <Button variant="primary" data-testid="confirmPromote"
                    onClick={handlePromoteCollab} disabled={isProcessing}>Confirm</Button>
            <Button variant="secondary" data-testid="cancelPromote"
                    onClick={() => onClose('')}>Cancel</Button>
          </div>
        </Box>
      </Dialog>
  )
}